// src/admin/pages/OpenHouse.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Grid,
    Typography,
    TextField,
    Button,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EmailIcon from '@mui/icons-material/Email';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import CrudTable from '../components/CrudTable';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { adminSendEmail, adminOpenHouse } from '../../utils/api';

/**
 * Simple US phone normalizer: 10 digits -> ###-###-####
 * 11 digits with leading 1 -> strip 1 then format.
 * Otherwise, return raw value.
 */
function formatPhone(p) {
    const digits = String(p || '').replace(/\D/g, '');
    if (/^1\d{10}$/.test(digits)) {
        return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (/^\d{10}$/.test(digits)) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return p || '';
}

const ReadonlyTF = (props) => (
    <TextField {...props} fullWidth variant="outlined" InputProps={{ readOnly: true }} />
);

/* ---------------- Email dialog (mobile full-screen) ---------------- */
function EmailDialog({ open, onClose, name, email, onSent }) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false); // <- fixed typo here

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (open) {
            setSubject('');
            setMessage('');
        }
    }, [open, name, email]);

    const send = async () => {
        if (!email) return alert('No email for this person.');
        setSending(true);
        try {
            const res = await adminSendEmail({ to: email, subject, message });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to send message.');
            onSent?.();
            onClose?.();
        } catch (e) {
            alert(e.message || 'Failed to send message.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose?.();
            }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Send Message</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <ReadonlyTF label="Name" value={name || ''} />
                <ReadonlyTF label="Email" value={email || ''} />
                <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                />
                <TextField
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    multiline
                    minRows={8}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={send} disabled={sending || !subject || !message}>
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ---------------- Mobile filter popup ---------------- */
function FilterDialog({ open, initial, onSearch, onClear, onCancel }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [local, setLocal] = useState(initial);

    useEffect(() => {
        if (open) setLocal(initial);
    }, [open, initial]);

    const set = (k) => (e) => setLocal((f) => ({ ...f, [k]: e.target.value }));

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onCancel?.();
            }}
            disableEscapeKeyDown
        >
            <DialogTitle>Filter Open House</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 1.25 }}>
                <TextField label="Name" value={local.q} onChange={set('q')} fullWidth />
                <TextField label="City" value={local.city} onChange={set('city')} fullWidth />
                <TextField label="Email contains" value={local.email} onChange={set('email')} fullWidth />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={() => onClear?.()}>Clear</Button>
                <Button variant="contained" onClick={() => onSearch?.(local)}>
                    Search
                </Button>
                <Button onClick={onCancel}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function OpenHousePage() {
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({ q: '', city: '', email: '' });
    const [filterDlgOpen, setFilterDlgOpen] = useState(false);

    // email dialog + snackbar
    const [emailDlg, setEmailDlg] = useState({ open: false, name: '', email: '' });
    const [snack, setSnack] = useState(false);

    // error banner
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    // Load rows using the same API helper pattern as other admin pages
    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await adminOpenHouse(); // uses configured API base + credentials
            setRows(Array.isArray(data?.rows) ? data.rows : []);
            if (!Array.isArray(data?.rows)) {
                setError('Unexpected response from server for Open House data.');
            }
        } catch (e) {
            setRows([]);
            setError(e?.message || 'Failed to load Open House data.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const f = { ...filters };
        Object.keys(f).forEach((k) => (f[k] = String(f[k] || '').trim().toLowerCase()));
        return rows.filter((r) => {
            const name = String(r.name || '').toLowerCase();
            const city = String(r.address_city || '').toLowerCase();
            const email = String(r.email || '').toLowerCase();
            if (f.q && !name.includes(f.q)) return false;
            if (f.city && !city.includes(f.city)) return false;
            if (f.email && !email.includes(f.email)) return false;
            return true;
        });
    }, [rows, filters]);

    const columns = [
        { key: 'name', header: 'Name', nowrap: true },
        {
            key: 'email',
            header: 'Email',
            nowrap: true,
            render: (v, r) =>
                v ? (
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<EmailIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setEmailDlg({ open: true, name: r.name, email: v });
                        }}
                    >
                        {v}
                    </Button>
                ) : (
                    '—'
                ),
        },
        {
            key: 'phone',
            header: 'Phone',
            nowrap: true,
            render: (v) => formatPhone(v),
        },
        { key: 'address_street', header: 'Street Address' },
        { key: 'address_city', header: 'City', nowrap: true },
        { key: 'address_state', header: 'State', nowrap: true },
        { key: 'address_zip', header: 'ZIP', nowrap: true },
    ];

    const handleDownloadPdf = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'pt',
            format: 'a4',
        });

        const title = 'Open House Report';
        const generatedAt = new Date().toLocaleString();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text(title, 40, 40);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(80);
        doc.text(`Generated: ${generatedAt}`, 40, 60);
        doc.text(`Total attendees: ${filtered.length}`, 40, 76);

        if (!filtered.length) {
            doc.setTextColor(120);
            doc.text('No records match the current filters.', 40, 100);
            doc.save('open-house-report.pdf');
            return;
        }

        const head = [[
            'Name',
            'Email',
            'Phone',
            'Street',
            'City',
            'State',
            'ZIP',
        ]];

        const body = filtered.map((row) => [
            row.name || '—',
            row.email || '—',
            formatPhone(row.phone),
            row.address_street || '—',
            row.address_city || '—',
            row.address_state || '—',
            row.address_zip || '—',
        ]);

        autoTable(doc, {
            head,
            body,
            startY: 96,
            styles: {
                fontSize: 9,
                cellPadding: 6,
                valign: 'middle',
                overflow: 'linebreak',
            },
            headStyles: {
                fillColor: [33, 150, 243],
                textColor: 255,
                fontSize: 10,
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245],
            },
            bodyStyles: {
                textColor: 40,
            },
            margin: { top: 80, left: 40, right: 40, bottom: 40 },
        });

        doc.save('open-house-report.pdf');
    };

    return (
        <Box>
            {/* Sticky header with filters */}
            <Paper
                elevation={1}
                sx={{ position: 'sticky', top: 0, zIndex: 5, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Open House
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadPdf}
                        >
                            Download PDF
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setFilterDlgOpen(true)}
                            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                        >
                            Filters
                        </Button>
                    </Box>
                </Box>

                {/* Desktop filters */}
                <Grid container spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            size="small"
                            label="Name"
                            fullWidth
                            value={filters.q}
                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            size="small"
                            label="City"
                            fullWidth
                            value={filters.city}
                            onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            size="small"
                            label="Email contains"
                            fullWidth
                            value={filters.email}
                            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Error banner (if any) */}
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Table (mobile -> auto card view via CrudTable responsive styles) */}
            <CrudTable
                ariaLabel="Open House attendees"
                columns={columns}
                rows={filtered}
                height="calc(100dvh - 280px)"
                loading={loading}
            />

            <EmailDialog
                open={emailDlg.open}
                name={emailDlg.name}
                email={emailDlg.email}
                onClose={() => setEmailDlg({ open: false, name: '', email: '' })}
                onSent={() => setSnack(true)}
            />

            {/* "Message sent." snackbar */}
            <Snackbar open={snack} autoHideDuration={3000} onClose={() => setSnack(false)}>
                <Alert severity="success" onClose={() => setSnack(false)}>
                    Message sent.
                </Alert>
            </Snackbar>

            {/* Mobile filter dialog */}
            <FilterDialog
                open={filterDlgOpen}
                initial={filters}
                onSearch={(vals) => {
                    setFilters(vals);
                    setFilterDlgOpen(false);
                }}
                onClear={() => {
                    setFilters({ q: '', city: '', email: '' });
                    setFilterDlgOpen(false);
                }}
                onCancel={() => setFilterDlgOpen(false)}
            />
        </Box>
    );
}
